// frontend/src/components/WorkflowTemplateCategoryManagerModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-16
// Updated:      2026-09-16
//
// Description:
//   Full-page modal for governed workflow template category management.
//
//   Responsibilities:
//     - Render above the entire page via React portal
//     - List workflow template categories
//     - Create workflow template category
//     - Edit workflow template category
//     - Deactivate workflow template category
//     - Prevent clipping inside grid/widget containers
//
//   Important:
//     - Must render to document.body, not inside widget DOM hierarchy
//     - Intended for use from WorkflowTemplateListWidget
//     - Modal is page-level, not widget-level
//
// Responsive pass (2026-09-16):
//     - Mobile/tablet uses a flat full-height management sheet; centred modal starts at lg.
//     - Category list and editor stack vertically below lg, preserving the desktop two-pane layout.
//     - Outer section framing is flattened on mobile to avoid card-inside-card presentation.
//     - Category actions and form actions become touch-friendly full-width controls on phones.
//     - No category loading, create, update, deactivate, selection, or callback behaviour changed.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx";
import {
  listWorkflowTemplateCategories,
  createWorkflowTemplateCategory,
  updateWorkflowTemplateCategory,
  deactivateWorkflowTemplateCategory,
} from "../api/workflowAdmin";

const emptyForm = {
  name: "",
  description: "",
  sort_order: 0,
  is_active: true,
};

const WorkflowTemplateCategoryManagerModal = ({
  open = false,
  onClose,
  onChanged,
}) => {
  const { theme } = useTheme();

  const [categories, setCategories] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(editingCategoryId)) || null,
    [categories, editingCategoryId]
  );

  const actionBtnStyle = {
    background: theme.button_action?.normal || "#ff7a00",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  };

  const generalBtnStyle = {
    background: theme.button_general?.normal || "#16324f",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  };

  const mutedBtnStyle = {
    background: "#e5e7eb",
    color: "#111827",
    border: "none",
    borderRadius: 8,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    boxSizing: "border-box",
    background: "#fff",
  };

  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 6,
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await listWorkflowTemplateCategories({
        include_inactive: includeInactive,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setCategories(items);

      if (editingCategoryId) {
        const stillExists = items.some((c) => String(c.id) === String(editingCategoryId));
        if (!stillExists) {
          setEditingCategoryId(null);
          setForm(emptyForm);
        }
      }
    } catch (err) {
      console.error(
        "Failed to load workflow template categories:",
        err?.response?.data || err?.message || err
      );
      alert("Failed to load workflow template categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, includeInactive]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  const startCreate = () => {
    setEditingCategoryId(null);
    setForm(emptyForm);
  };

  const startEdit = (category) => {
    setEditingCategoryId(category.id);
    setForm({
      name: category.name || "",
      description: category.description || "",
      sort_order: Number(category.sort_order || 0),
      is_active: !!category.is_active,
    });
  };

  const handleSave = async () => {
    const payload = {
      name: String(form.name || "").trim(),
      description: String(form.description || "").trim() || null,
      sort_order: Number(form.sort_order || 0),
      is_active: !!form.is_active,
    };

    if (!payload.name) {
      alert("Category name is required.");
      return;
    }

    setSaving(true);
    try {
      if (editingCategoryId) {
        await updateWorkflowTemplateCategory(editingCategoryId, payload);
      } else {
        const created = await createWorkflowTemplateCategory(payload);
        setEditingCategoryId(created?.id || null);
      }

      await loadCategories();
      onChanged?.();
    } catch (err) {
      console.error(
        "Failed to save workflow template category:",
        err?.response?.data || err?.message || err
      );
      const detail =
        err?.response?.data?.detail || "Failed to save workflow template category.";
      alert(typeof detail === "string" ? detail : "Failed to save workflow template category.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (category) => {
    if (!category?.id) return;

    const confirmed = window.confirm(
      `Deactivate category "${category.name}"?`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await deactivateWorkflowTemplateCategory(category.id, {});
      await loadCategories();
      onChanged?.();

      if (String(editingCategoryId) === String(category.id)) {
        setEditingCategoryId(null);
        setForm(emptyForm);
      }
    } catch (err) {
      console.error(
        "Failed to deactivate workflow template category:",
        err?.response?.data || err?.message || err
      );
      const detail =
        err?.response?.data?.detail || "Failed to deactivate workflow template category.";
      alert(
        typeof detail === "string"
          ? detail
          : "Failed to deactivate workflow template category."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Workflow Template Category Manager"
      className="fixed inset-0 z-[100000] flex items-stretch justify-center bg-black/45 p-0 lg:items-center lg:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        className="flex h-[100dvh] w-full min-w-0 flex-col overflow-hidden bg-white shadow-none lg:h-[min(760px,92vh)] lg:w-[min(1100px,96vw)] lg:rounded-2xl lg:shadow-[0_24px_80px_rgba(0,0,0,0.28)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-3 py-3 sm:px-4 lg:px-6 lg:pb-[14px] lg:pt-5">
          <div className="min-w-0 flex-1">
            <div
              className="mb-1 break-words text-lg font-extrabold"
              style={{ color: theme.accent_color || "#ff7a00" }}
            >
              Manage Categories
            </div>
            <div className="break-words text-[13px] text-gray-500">
              Governed categories for workflow templates.
            </div>
          </div>

          <button
            type="button"
            onClick={() => onClose?.()}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-gray-200 text-[22px] leading-none text-gray-900 hover:bg-gray-300"
          >
            ×
          </button>
        </div>

        {/* Main workspace */}
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4 lg:grid lg:grid-cols-[minmax(420px,1.3fr)_minmax(320px,0.9fr)] lg:gap-6 lg:overflow-hidden lg:p-6">
          {/* Category list */}
          <section className="flex min-w-0 flex-col border-b border-gray-200 pb-4 lg:min-h-0 lg:overflow-hidden lg:rounded-[14px] lg:border lg:border-gray-200 lg:pb-0">
            <div className="flex shrink-0 flex-col gap-3 pb-3 lg:flex-row lg:items-center lg:justify-between lg:border-b lg:border-gray-200 lg:px-[18px] lg:py-4">
              <div className="text-[15px] font-extrabold">Existing Categories</div>

              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <label className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  />
                  Include inactive
                </label>

                <button
                  type="button"
                  onClick={startCreate}
                  className="w-full sm:w-auto"
                  style={generalBtnStyle}
                >
                  New Category
                </button>
              </div>
            </div>

            <div className="flex min-h-0 flex-col gap-2.5 pt-1 lg:flex-1 lg:overflow-y-auto lg:p-4">
              {loading ? (
                <div className="text-sm text-gray-500">Loading categories...</div>
              ) : categories.length === 0 ? (
                <div className="text-sm text-gray-500">No categories found.</div>
              ) : (
                categories.map((category) => {
                  const isSelected =
                    String(editingCategoryId) === String(category.id);

                  return (
                    <div
                      key={category.id}
                      className="min-w-0 rounded-xl p-3 sm:p-[14px]"
                      style={{
                        border: isSelected
                          ? `2px solid ${theme.accent_color || "#ff7a00"}`
                          : "1px solid #e5e7eb",
                        background: isSelected ? "#fff7ed" : "#fff",
                      }}
                    >
                      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="break-words text-[15px] font-extrabold text-gray-900">
                            {category.name}
                          </div>

                          {category.description ? (
                            <div className="mt-1 whitespace-pre-wrap break-words text-[13px] text-gray-500">
                              {category.description}
                            </div>
                          ) : null}

                          <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1 text-xs text-gray-500">
                            <span>Sort: {Number(category.sort_order || 0)}</span>
                            <span>ID: {category.id}</span>
                          </div>
                        </div>

                        <div className="flex min-w-0 flex-col gap-2 sm:shrink-0 sm:items-end">
                          <span
                            className="self-start rounded-full px-2.5 py-1 text-xs font-extrabold sm:self-auto"
                            style={{
                              background: category.is_active ? "#dcfce7" : "#f3f4f6",
                              color: category.is_active ? "#166534" : "#4b5563",
                            }}
                          >
                            {category.is_active ? "Active" : "Inactive"}
                          </span>

                          <div className="grid grid-cols-1 gap-2 sm:flex">
                            <button
                              type="button"
                              onClick={() => startEdit(category)}
                              className="w-full sm:w-auto"
                              style={mutedBtnStyle}
                            >
                              Edit
                            </button>

                            {category.is_active ? (
                              <button
                                type="button"
                                onClick={() => handleDeactivate(category)}
                                className="w-full sm:w-auto"
                                style={{
                                  ...mutedBtnStyle,
                                  background: "#fee2e2",
                                  color: "#991b1b",
                                }}
                              >
                                Deactivate
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Editor */}
          <section className="mt-4 flex min-w-0 flex-col pt-4 lg:mt-0 lg:min-h-0 lg:overflow-hidden lg:rounded-[14px] lg:border lg:border-gray-200 lg:pt-0">
            <div className="shrink-0 pb-3 text-[15px] font-extrabold lg:border-b lg:border-gray-200 lg:px-[18px] lg:py-4">
              {selectedCategory ? "Edit Category" : "Create Category"}
            </div>

            <div className="min-h-0 lg:flex-1 lg:overflow-y-auto lg:p-4">
              <div className="mb-[14px] min-w-0">
                <label style={labelStyle}>Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="Category name"
                  style={{ ...inputStyle, minWidth: 0 }}
                />
              </div>

              <div className="mb-[14px] min-w-0">
                <label style={labelStyle}>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Optional description"
                  style={{
                    ...inputStyle,
                    minWidth: 0,
                    minHeight: 110,
                    resize: "vertical",
                  }}
                />
              </div>

              <div className="mb-[14px] min-w-0">
                <label style={labelStyle}>Sort Order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      sort_order: Number(e.target.value || 0),
                    }))
                  }
                  style={{ ...inputStyle, minWidth: 0 }}
                />
              </div>

              <div className="mb-[18px]">
                <label className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                  Active
                </label>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full sm:w-auto"
                  style={{
                    ...actionBtnStyle,
                    opacity: saving ? 0.7 : 1,
                    cursor: saving ? "progress" : "pointer",
                  }}
                >
                  {saving
                    ? "Saving..."
                    : selectedCategory
                      ? "Save Category"
                      : "Create Category"}
                </button>

                <button
                  type="button"
                  onClick={startCreate}
                  className="w-full sm:w-auto"
                  style={mutedBtnStyle}
                >
                  Clear
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkflowTemplateCategoryManagerModal;
